import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TrackingService } from 'analytics';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { LegalDocument } from 'shared';

/**
 * Wraps the /api/funnel/* endpoints. The session_token (JWT) is stored in
 * localStorage so the quiz survives reloads, browser-back out of the app and
 * links opened in a new tab. If it's missing or expired, the backend falls
 * back to the `cid` auth cookie and re-issues a session_token in the response.
 *
 * Backend contract: see backend/app/controllers/api/funnel/{leads,quiz,checkout}_controller.rb
 */

export type LeadStatus = 'email_sent' | 'redirect_login';

export interface LeadResponse {
  status:           LeadStatus;
  message?:         string;
  login_url?:       string;
  /** true when the email matches a user who already owns a FAC3LESS card */
  existing_account?: boolean;
}

export interface QuizOption {
  value:    string;
  i18n_key: string;
}

export interface QuizQuestion {
  key:       string;
  i18n_key:  string;
  options:   QuizOption[];
  progress:  { current: number; total: number };
}

export interface QuizProduct {
  id:          number;
  code:        string;
  name:        string;
  price:       number | string;
  /**
   * Set only for a product whose `price` is not the whole price the customer is
   * quoted — today the fac3less card, whose activation fee is paid directly to the
   * card partner in the card dashboard and never charged here.
   */
  activation_fee?:       number | string | null;
  /** `price` + `activation_fee`; equals `price` when there is no fee. */
  total_customer_price?: number | string;
  description: string;
}

export type QuizStatus = 'started' | 'completed' | 'purchased' | 'abandoned';

export interface QuizSession {
  session_id:                 number;
  status:                     QuizStatus;
  answers:                    Record<string, string>;
  session_token?:             string;
  next_question:              QuizQuestion | null;
  recommended_product_code?:  string;
  product?:                   QuizProduct;
}

export interface CheckoutResponse {
  order_id:         number;
  status:           string;
  payment_method:   string;
  total_price:      number | string;
  total_with_costs: number | string;
  product:          { code: string; name: string };
}

export interface PasswordSetupResponse {
  status: string;
  /** Address the password-setup link was emailed to (the funnel user's own). */
  email?: string;
}

// ----- Direct checkout (no registration before the purchase) --------------
// Same request shape as Global Health's POST /funnel/checkout/start; for this
// brand the only plan slug is `platinum` (FAC3LESS Platinum Card).

/** The documents a buyer accepts on the direct-checkout screen. */
export interface CheckoutLegalResponse {
  documents: LegalDocument[];
}

export interface CheckoutStartRequest {
  first_name:    string;
  last_name:     string;
  email:         string;
  language:      string;
  sponsor_id:    string | null;
  plan:          string;
  weeks:         number | null;
  journey:       string | null;
  goal:          string | null;
  captcha_token: string | null;
  consents:      { key: string; version: string }[];
}

/** `next` is the complete in-app path incl. language (e.g.
 *  `/en/order-wizard?add=12&step=2`) — navigated to verbatim, never rebuilt here. */
export type CheckoutStartResponse =
  | { status: 'ok'; next: string; first_name?: string }
  | { status: 'existing_account'; has_password?: boolean };

const SESSION_TOKEN_KEY = 'fac3less.funnel.session_token';

@Injectable({ providedIn: 'root' })
export class FunnelService {
  private http = inject(HttpClient);
  private tracking = inject(TrackingService);

  // ----- Leads ------------------------------------------------------------

  createLead(input: {
    first_name:     string;
    last_name?:     string;
    email:          string;
    sponsor_id?:    string | number | null;
    language?:      string;
    captcha_token?: string | null;
  }): Observable<LeadResponse> {
    return this.http.post<LeadResponse>('/funnel/leads', input);
  }

  // ----- Direct checkout ----------------------------------------------------

  /** The legal documents the direct checkout asks the buyer to accept (public). */
  getCheckoutLegal(language: string): Observable<CheckoutLegalResponse> {
    return this.http.get<CheckoutLegalResponse>('/funnel/checkout/legal', { params: { language } });
  }

  /** Creates (or recognises) the account from name + e-mail; for a new account the
   *  backend also sets the soft-login `cid` cookie so the order wizard can take the
   *  payment right away. */
  startCheckout(input: CheckoutStartRequest): Observable<CheckoutStartResponse> {
    // tracking_session_id: Tracking Engine V1 — links the landing session (?wps=)
    // to the account. Optional; the backend ignores an unknown id.
    return this.http.post<CheckoutStartResponse>('/funnel/checkout/start',
      { ...input, tracking_session_id: this.tracking.sessionId });
  }

  /** Re-sends the confirmation mail of an account created by the direct checkout. */
  resendAccountMail(): Observable<{ status: 'sent' | 'already_confirmed' }> {
    return this.http.post<{ status: 'sent' | 'already_confirmed' }>('/funnel/checkout/resend_account_mail', {});
  }

  /** Consumes the token of the account mail (/join/start?token=…): confirms the
   *  e-mail and sets the soft-login cookie. No quiz session is opened. */
  startMembershipFunnel(
    magicToken: string, sponsorId?: string | null
  ): Observable<{ account_exists: boolean; first_name?: string }> {
    return this.http.post<{ account_exists: boolean; first_name?: string }>(
      '/funnel/membership/start', {
        token: magicToken,
        ...(sponsorId ? { sponsor_id: sponsorId } : {}),
        // Tracking Engine V1: links the landing session to the account (optional,
        // the backend ignores an unknown id).
        tracking_session_id: this.tracking.sessionId
      }
    );
  }

  /** Sets the password of the soft-logged-in user; returns the address to log in with. */
  setMembershipPassword(password: string): Observable<{ status: string; email: string }> {
    return this.http.post<{ status: string; email: string }>('/funnel/membership/set_password', { password });
  }

  // ----- Quiz -------------------------------------------------------------

  startQuiz(magicToken: string): Observable<QuizSession> {
    return this.http.post<QuizSession>('/funnel/quiz/start', { token: magicToken });
  }

  showQuiz(): Observable<QuizSession> {
    return this.http.get<QuizSession>('/funnel/quiz', { headers: this.sessionHeaders() });
  }

  answerQuiz(key: string, value: string): Observable<QuizSession> {
    return this.http.post<QuizSession>(
      '/funnel/quiz/answer',
      { key, value },
      { headers: this.sessionHeaders() }
    );
  }

  /** Re-asks question `step` (1-based), discarding its answer and all later ones. */
  rewindQuiz(step: number): Observable<QuizSession> {
    return this.http.post<QuizSession>(
      '/funnel/quiz/rewind',
      { step },
      { headers: this.sessionHeaders() }
    );
  }

  /**
   * Emails the soft-logged-in funnel user a password-setup / login link so they
   * can create their account without buying first (or to finish later if a
   * purchase failed). The backend resolves the user from the funnel session.
   */
  requestPasswordSetup(): Observable<PasswordSetupResponse> {
    return this.http.post<PasswordSetupResponse>(
      '/funnel/quiz/password_setup',
      {},
      { headers: this.sessionHeaders() }
    );
  }

  // ----- Checkout ---------------------------------------------------------

  createCheckout(paymentMethod: string): Observable<CheckoutResponse> {
    return this.http.post<CheckoutResponse>(
      '/funnel/quiz/checkout',
      { payment_method: paymentMethod },
      { headers: this.sessionHeaders() }
    );
  }

  // ----- Session token ----------------------------------------------------

  storeSessionToken(token: string): void {
    try { localStorage.setItem(SESSION_TOKEN_KEY, token); } catch { /* ignore */ }
  }

  getSessionToken(): string | null {
    // sessionStorage read kept as fallback for sessions started before the
    // switch to localStorage.
    try {
      return localStorage.getItem(SESSION_TOKEN_KEY)
          ?? sessionStorage.getItem(SESSION_TOKEN_KEY);
    } catch { return null; }
  }

  clearSessionToken(): void {
    try { localStorage.removeItem(SESSION_TOKEN_KEY); } catch { /* ignore */ }
    try { sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch { /* ignore */ }
  }

  private sessionHeaders(): HttpHeaders {
    const token = this.getSessionToken() ?? '';
    return new HttpHeaders({ 'X-Funnel-Session': token });
  }
}
